// frontend/src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const [isInitialized, setIsInitialized] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const initAuth = async () => {
      if (isInitialized) return; // Prevent multiple initializations

      console.log('[Auth] Initializing authentication...');
      const storedToken = localStorage.getItem('access_token');

      if (storedToken) {
        try {
          console.log('[Auth] Validating stored token...');
          const response = await axios.get(`${API_BASE_URL}/api/v1/auth/me`);
          setUser(response.data);
          setToken(storedToken);
          console.log('[Auth] Token validation successful');
        } catch (error) {
          console.warn('[Auth] Token validation failed:', error.response?.status);
          // Only logout if it's a real auth error, not a network error
          if (error.response?.status === 401 || error.response?.status === 403) {
            logout();
          }
        }
      } else {
        console.log('[Auth] No stored token found');
      }

      setLoading(false);
      setIsInitialized(true);
    };

    initAuth();
  }, []); // Empty dependency array - only run once

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/v1/auth/login`, {
        email,
        password,
      });

      const { access_token, refresh_token } = response.data;
      
      // Store tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      // Set token in state and axios headers
      setToken(access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Get user info
      const userResponse = await axios.get(`${API_BASE_URL}/api/v1/auth/me`);
      setUser(userResponse.data);

      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/v1/auth/register`, userData);

      // Auto-login after successful registration
      const loginResult = await login(userData.email, userData.password);

      return loginResult.success
        ? { success: true, user: response.data }
        : { success: false, error: 'Registration successful but login failed' };
    } catch (error) {
      console.error('Registration failed:', error);

      let errorMessage = 'Registration failed';

      if (error.response?.data) {
        const errorData = error.response.data;

        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Handle Pydantic validation errors
          errorMessage = errorData.detail.map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return err.msg;
            if (err.message) return err.message;
            return 'Validation error';
          }).join(', ');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API_BASE_URL}/api/v1/auth/logout`);
      }
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      // Clear local storage and state regardless of API call result
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setToken(null);
      setUser(null);
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  const refreshToken = async () => {
    // Prevent multiple simultaneous refresh attempts
    if (isRefreshing) {
      console.log('[Auth] Token refresh already in progress, waiting...');
      // Wait for current refresh to complete
      return new Promise((resolve) => {
        const checkRefresh = () => {
          if (!isRefreshing) {
            resolve(!!localStorage.getItem('access_token'));
          } else {
            setTimeout(checkRefresh, 100);
          }
        };
        checkRefresh();
      });
    }

    setIsRefreshing(true);

    try {
      const refreshTokenValue = localStorage.getItem('refresh_token');
      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }

      console.log('[Auth] Refreshing access token...');
      const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
        refresh_token: refreshTokenValue,
      });

      const { access_token, refresh_token: newRefreshToken } = response.data;

      // Update stored tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', newRefreshToken);

      // Update state and axios headers
      setToken(access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      console.log('[Auth] Token refresh successful');
      return true;
    } catch (error) {
      console.error('[Auth] Token refresh failed:', error);
      logout();
      return false;
    } finally {
      setIsRefreshing(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/v1/auth/profile`, profileData);
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Profile update failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Profile update failed',
      };
    }
  };

  const changePassword = async (passwordData) => {
    try {
      await axios.post(`${API_BASE_URL}/api/v1/auth/change-password`, passwordData);
      return { success: true };
    } catch (error) {
      console.error('Password change failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Password change failed',
      };
    }
  };

  // Setup axios interceptor for automatic token refresh
  useEffect(() => {
    if (!isInitialized) return; // Only setup interceptor after initialization

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Avoid intercepting auth endpoints to prevent loops
        if (originalRequest.url?.includes('/auth/')) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry && !isRefreshing) {
          originalRequest._retry = true;

          console.log('[Auth] 401 error detected, attempting token refresh...');
          const refreshSuccess = await refreshToken();

          if (refreshSuccess) {
            // Retry the original request with new token
            originalRequest.headers['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
            console.log('[Auth] Retrying original request with new token');
            return axios(originalRequest);
          } else {
            console.log('[Auth] Token refresh failed, redirecting to login');
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [isInitialized]); // Depend on isInitialized

  // Handle page visibility changes to avoid unnecessary requests when page is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user && token) {
        // Only validate token if page has been hidden for more than 5 minutes
        const lastActivity = localStorage.getItem('last_activity');
        const now = Date.now();

        if (lastActivity && (now - parseInt(lastActivity)) > 5 * 60 * 1000) {
          console.log('[Auth] Page visible after long absence, validating token...');
          // Validate token silently
          axios.get(`${API_BASE_URL}/api/v1/auth/me`)
            .catch((error) => {
              if (error.response?.status === 401) {
                console.log('[Auth] Token expired during absence, logging out');
                logout();
              }
            });
        }

        localStorage.setItem('last_activity', now.toString());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Set initial activity timestamp
    localStorage.setItem('last_activity', Date.now().toString());

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, token]);

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshToken,
    updateProfile,
    changePassword,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isResearcher: user?.role === 'researcher' || user?.role === 'admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
